import React, { useEffect, useState } from 'react';
import { Button, Typography, Spin, theme } from 'antd';
import { IconArrowLeft, IconScale } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { message } from '../../services/toastMessage';
import AppLayout from '../../components/Layouts/Layout';
import PageColumn from '../../components/Layouts/PageColumn';
import EmptyState from '../../components/EmptyState';
import DisputeStatusCard from '../../components/DisputeStatusCard';
import DisputeOfferList from '../../components/DisputeOfferList';
import DisputeEvidenceGrid from '../../components/DisputeEvidenceGrid';
import DisputeAmountSheet from '../../components/DisputeAmountSheet';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import { getBookingById } from '../../services/BookingService';
import {
  getBookingDispute,
  openBookingDispute,
  respondToDispute,
  offerDisputeAmount,
  acceptDisputeOffer,
  escalateDispute,
  withdrawDispute,
  uploadDisputeEvidence,
} from '../../services/DisputeService';
import { IBooking } from '../../interfaces/heavyDutyMachinery/IBooking';
import { IBookingDispute } from '../../interfaces/heavyDutyMachinery/IBookingDispute';
import {
  DISPUTE_ACTION_LABEL_KEYS,
  DisputeAction,
  escalateLabelKey,
  isBelowReviewThreshold,
  isDisputeLive,
  latestOffer,
  nextDisputeAction,
} from '../../helpers/disputeHelper';
import { useAuth } from '../../contextProviders/AuthProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Title, Text } = Typography;

/**
 * One booking's claim, for whichever of the two parties is looking.
 *
 * Deliberately the same shape as BookingTimelinePage -- same 480px column,
 * same back link, same stack of cards -- because it is the same kind of
 * screen: a state somebody needs to understand, plus the one thing they
 * can do about it. The action lives at the bottom as a single full-width
 * button rather than scattered through the cards, so "what am I being
 * asked to do" never depends on how far you scrolled.
 *
 * The page renders before a dispute exists, too: with no claim on the
 * booking it becomes the place to file one.
 */
const BookingDisputePage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { userId } = useAuth();

  const [booking, setBooking] = useState<IBooking | undefined>(undefined);
  const [dispute, setDispute] = useState<IBookingDispute | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sheet, setSheet] = useState<'open' | 'respond' | 'offer' | null>(null);
  const [confirming, setConfirming] = useState<'acceptOffer' | 'escalate' | 'withdraw' | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([getBookingById(id), getBookingDispute(id).catch(() => null)])
      .then(([loadedBooking, loadedDispute]) => {
        if (cancelled) return;
        setBooking(loadedBooking);
        setDispute(loadedDispute);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Every action returns the whole dispute, so the page never has to patch
  // its own state field by field -- the server's answer is the state.
  const run = async (work: () => Promise<IBookingDispute>) => {
    setBusy(true);
    try {
      setDispute(await work());
      message.success(translate(t, TRANSLATIONS.DISPUTE_ACTION_SUCCESS));
      setSheet(null);
      setConfirming(null);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.DISPUTE_ACTION_ERROR));
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (file: File) => {
    if (!id) return;
    setUploading(true);
    try {
      setDispute(await uploadDisputeEvidence(id, file));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.DISPUTE_EVIDENCE_UPLOAD_ERROR));
    } finally {
      setUploading(false);
    }
  };

  const backButton = (
    <Button
      type="text"
      size="small"
      icon={<IconArrowLeft size={16} />}
      style={{ marginLeft: -8, marginBottom: 8 }}
      onClick={() => navigate(-1)}
    >
      {translate(t, TRANSLATIONS.BOOKING_TIMELINE_BACK_BUTTON)}
    </Button>
  );

  if (loading) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  if (!booking || !id) {
    return (
      <AppLayout>
        <PageColumn>
          {backButton}
          <EmptyState
            icon={<IconScale size={24} />}
            title={translate(t, TRANSLATIONS.DISPUTE_NOT_LOADED)}
            description={translate(t, TRANSLATIONS.DISPUTE_NOT_LOADED_DESCRIPTION)}
          />
        </PageColumn>
      </AppLayout>
    );
  }

  const action: DisputeAction = dispute ? nextDisputeAction(dispute, userId ?? undefined) : 'none';
  const offer = dispute ? latestOffer(dispute) : undefined;
  // "Escalate" is one action with two very different consequences -- a
  // reviewer above the threshold, the split rule below it -- so the button
  // has to say which one the tap is buying. See escalateLabelKey.
  const actionLabelKey =
    action === 'none'
      ? undefined
      : action === 'escalate' && dispute
        ? escalateLabelKey(dispute)
        : DISPUTE_ACTION_LABEL_KEYS[action];

  const handleAction = () => {
    switch (action) {
      case 'respond':
        setSheet('respond');
        break;
      case 'offer':
        setSheet('offer');
        break;
      case 'acceptOffer':
      case 'escalate':
      case 'withdraw':
        setConfirming(action);
        break;
      default:
        break;
    }
  };

  return (
    <AppLayout>
      <PageColumn>
        {backButton}
        <Title level={4} style={{ margin: '0 0 4px' }}>
          {translate(t, TRANSLATIONS.DISPUTE_PAGE_TITLE)}
        </Title>
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 20 }}>
          {booking.heavyDutyMachineryTitle}
        </Text>

        {dispute ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <DisputeStatusCard dispute={dispute} currencyCode={booking.hostCurrencyCode} userId={userId ?? undefined} />
            <DisputeOfferList offers={dispute.offers} currencyCode={booking.hostCurrencyCode} userId={userId ?? undefined} />
            <DisputeEvidenceGrid
              bookingId={id}
              evidence={dispute.evidence}
              userId={userId ?? undefined}
              onUpload={isDisputeLive(dispute) ? handleUpload : undefined}
              uploading={uploading}
            />

            {action !== 'none' ? (
              <Button
                block
                type="primary"
                size="large"
                danger={action === 'withdraw'}
                loading={busy}
                onClick={handleAction}
                style={{ borderRadius: 999 }}
              >
                {translate(t, actionLabelKey!)}
              </Button>
            ) : (
              isDisputeLive(dispute) && (
                <Text type="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
                  {translate(t, TRANSLATIONS.DISPUTE_WAITING_OTHER)}
                </Text>
              )
            )}
          </div>
        ) : (
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: 16,
              padding: 16,
            }}
          >
            <Title level={5} style={{ margin: '0 0 6px' }}>
              {translate(t, TRANSLATIONS.DISPUTE_OPEN_FROM_BOOKING)}
            </Title>
            <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
              {translate(t, TRANSLATIONS.DISPUTE_OPEN_SUBTITLE)}
            </Text>
            <Button block type="primary" size="large" onClick={() => setSheet('open')} style={{ borderRadius: 999 }}>
              {translate(t, TRANSLATIONS.DISPUTE_OPEN_CONFIRM)}
            </Button>
          </div>
        )}
      </PageColumn>

      <DisputeAmountSheet
        open={sheet === 'open'}
        titleKey={TRANSLATIONS.DISPUTE_OPEN_TITLE}
        subtitleKey={TRANSLATIONS.DISPUTE_OPEN_SUBTITLE}
        amountLabelKey={TRANSLATIONS.DISPUTE_OPEN_AMOUNT_LABEL}
        textPlaceholderKey={TRANSLATIONS.DISPUTE_OPEN_REASON_PLACEHOLDER}
        confirmKey={TRANSLATIONS.DISPUTE_OPEN_CONFIRM}
        submitting={busy}
        onClose={() => setSheet(null)}
        onSubmit={({ amount, text }) => run(() => openBookingDispute(id, { reason: text, claimedAmount: amount! }))}
      />

      <DisputeAmountSheet
        open={sheet === 'respond'}
        titleKey={TRANSLATIONS.DISPUTE_RESPOND_TITLE}
        textPlaceholderKey={TRANSLATIONS.DISPUTE_RESPOND_PLACEHOLDER}
        confirmKey={TRANSLATIONS.DISPUTE_ACTION_RESPOND}
        submitting={busy}
        onClose={() => setSheet(null)}
        onSubmit={({ text }) => run(() => respondToDispute(id, text))}
      />

      <DisputeAmountSheet
        open={sheet === 'offer'}
        titleKey={TRANSLATIONS.DISPUTE_OFFER_TITLE}
        subtitleKey={TRANSLATIONS.DISPUTE_OFFER_SUBTITLE}
        amountLabelKey={TRANSLATIONS.DISPUTE_CURRENT_OFFER_LABEL}
        textPlaceholderKey={TRANSLATIONS.DISPUTE_OFFER_NOTE_PLACEHOLDER}
        confirmKey={TRANSLATIONS.DISPUTE_ACTION_OFFER}
        initialAmount={offer?.amount ?? dispute?.claimedAmount}
        requireText={false}
        submitting={busy}
        onClose={() => setSheet(null)}
        onSubmit={({ amount, text }) => run(() => offerDisputeAmount(id, amount!, text || undefined))}
      />

      <ConfirmBottomSheet
        open={confirming !== null}
        title={confirming ? translate(t, actionLabelKey ?? DISPUTE_ACTION_LABEL_KEYS[confirming]) : ''}
        description={
          confirming === 'acceptOffer'
            ? offer?.note
            : confirming === 'escalate' && dispute
              ? translate(t, isBelowReviewThreshold(dispute) ? TRANSLATIONS.DISPUTE_DEFAULT_SPLIT : TRANSLATIONS.DISPUTE_DEFAULT_REVIEW)
              : undefined
        }
        cancelText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_KEEP)}
        confirmText={confirming ? translate(t, actionLabelKey ?? DISPUTE_ACTION_LABEL_KEYS[confirming]) : ''}
        confirmLoading={busy}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming === 'acceptOffer' && offer) run(() => acceptDisputeOffer(id, offer.id));
          else if (confirming === 'escalate') run(() => escalateDispute(id));
          else if (confirming === 'withdraw') run(() => withdrawDispute(id));
        }}
      />
    </AppLayout>
  );
};

export default BookingDisputePage;
